import React, { useState } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import AxiosUrl from "../../AxiosUrl";
import { toast } from "react-toastify";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./DeleteUserModal.css";

function DeleteUserModal({ open, onClose, userData, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");

  // Function to handle user deletion
  const handleDelete = () => {
    if (!userData || !userData._id) {
      toast.error("Invalid user data");
      return;
    }

    if (!deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }

    setLoading(true);
    const uid = localStorage.getItem("id");
    const adminName = localStorage.getItem("name") || "Admin";

    // API call to delete user and log the action
    AxiosUrl.delete(`/delete-user/${uid}/${userData._id}`, {
      data: { 
        reason: deleteReason, 
        adminId: uid,
        adminName: adminName,
        deletedAt: new Date().toISOString(),
        userData: {
          userId: userData._id,
          username: userData.username,
          email: userData.email,
          mobile: userData.mobile
        }
      }
    })
      .then((res) => {
        toast.success(res.data.message || "User deleted successfully");
        onSuccess && onSuccess();
        onClose();
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        toast.error(
          error.response?.data?.error || "Failed to delete user"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      open={open}
      onClose={loading ? null : onClose}
      aria-labelledby="delete-user-modal-title"
      aria-describedby="delete-user-modal-description"
    >
      <Box className={`delete-user-modal ${isMobile ? 'delete-user-modal-mobile' : ''}`}>
        <div className="delete-user-modal-header">
          <Typography variant="h6" component="h2" className="delete-user-modal-title">
            Confirm User Deletion
          </Typography>
          {!loading && (
            <CloseIcon 
              className="delete-user-modal-close-icon" 
              onClick={onClose}
            />
          )}
        </div>

        <div className="delete-user-modal-content">
          <div className="delete-user-modal-warning-container">
            <WarningIcon className="delete-user-modal-warning-icon" />
            <Typography className="delete-user-modal-message">
              Are you sure you want to delete this user? This action cannot be undone.
            </Typography>
          </div>
          
          {userData && (
            <div className="delete-user-modal-details">
              <div className="delete-user-modal-detail-item">
                <span className="delete-user-modal-detail-label">User ID:</span>
                <span className="delete-user-modal-detail-value">
                  {userData._id || "N/A"}
                </span>
              </div>
              <div className="delete-user-modal-detail-item">
                <span className="delete-user-modal-detail-label">Username:</span>
                <span className="delete-user-modal-detail-value">
                  {userData.username || "N/A"}
                </span>
              </div>
              <div className="delete-user-modal-detail-item">
                <span className="delete-user-modal-detail-label">Email:</span>
                <span className="delete-user-modal-detail-value">
                  {userData.email || "N/A"}
                </span>
              </div>
              <div className="delete-user-modal-detail-item">
                <span className="delete-user-modal-detail-label">Mobile:</span>
                <span className="delete-user-modal-detail-value">
                  {userData.mobile || "N/A"}
                </span>
              </div>
              <div className="delete-user-modal-detail-item">
                <span className="delete-user-modal-detail-label">Status:</span>
                <span className="delete-user-modal-detail-value">
                  {userData.userStatus === 0
                    ? "Active"
                    : userData.userStatus === 1
                    ? "Deactive"
                    : "Blocked"}
                </span>
              </div>
            </div>
          )}
          
          <div className="delete-user-modal-reason-container">
            <Typography className="delete-user-modal-reason-label">
              Reason for deletion (required):
            </Typography>
            <textarea 
              className="delete-user-modal-reason-input"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Please provide a reason for deleting this user"
              rows={3}
              required
            />
          </div>
          
          <Typography className="delete-user-modal-warning">
            This action will permanently delete the user account and all associated data.
          </Typography>
        </div>

        <div className="delete-user-modal-actions">
          <Button
            onClick={onClose}
            disabled={loading}
            className="delete-user-modal-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={loading || !deleteReason.trim()}
            className="delete-user-modal-delete-button"
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <DeleteIcon className="delete-user-modal-delete-icon" />
                Delete User
              </>
            )}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

export default DeleteUserModal; 